import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GroupedBarChartComponent } from '../../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { AuthService, FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { SummaryRequest } from '../../../../../../_shared/models/summary-request.model';
import { hasCompleteDateRange, isSameWidgetFilter } from '../../../../../../_shared/helpers/widget-filter.helper';
import { MaintenanceInsightsResponse } from '../../../../maintenance-dashboard/models/maintenance-dashboard-response.model';
import { combineKpiInsights } from '../../../helpers/kpi-insights.helper';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../../_shared/helpers/object.helper';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatValue } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-monthly-cost-comparison-modal',
  standalone: true,
  imports: [CommonModule, GroupedBarChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, DateFilterComponent, TranslocoPipe],
  templateUrl: './monthly-cost-comparison-modal.component.html',
  styleUrl: './monthly-cost-comparison-modal.component.scss'
})
export class MonthlyCostComparisonModalComponent implements OnInit, OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private costingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);
  private widgetFilter: WidgetFilterData = { ...this.filterService.filter };

  chartData: GroupedChartData[] = [];
  readonly barWidth = 16;
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly subgroups = [
    { name: 'inHouse', legend: this.transloco.translate('maintenanceCosting.charts.monthlyCostComparison.legend.inHouse'), color: '#8D63C3' },
    { name: 'outsourced', legend: this.transloco.translate('maintenanceCosting.charts.monthlyCostComparison.legend.outsourced'), color: '#F4BC2B' }
  ];

  isLoading: boolean = true;
  // Set by the opening card to the page's current selection
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  currencySymbol: string = '';
  currencyCode: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  get title(): string {
    return this.transloco.translate('maintenanceCosting.charts.monthlyCostComparison.title');
  }

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  async ngOnInit(): Promise<void> {
    await this.loadCostData();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (isEmptyObject(this.dateFilter) || isEmptyObject(this.accountFilter)) {
      return;
    }

    const filter = {
      ...this.dateFilter,
      accountIds: [...this.accountFilter.accountIds]
    } as WidgetFilterData;

    // The account filter re-emits the selection the modal opened with once its accounts load
    if (isSameWidgetFilter(filter, this.widgetFilter)) {
      return;
    }

    this.widgetFilter = filter;
    await this.loadCostData();
  }

  async loadCostData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const [inHouseResponse, outsourcedResponse] = await Promise.all([
        this.costingService.MonthlyInHouseCostKpi(request, this.cancelSubject),
        this.costingService.MonthlyOutsourceKpi(request, this.cancelSubject)
      ]);

      const summary = combineKpiInsights([inHouseResponse.data, outsourcedResponse.data]);
      const inHouseData = inHouseResponse.data?.data ?? [];
      const outsourcedData = outsourcedResponse.data?.data ?? [];

      this.totalValue = summary.totalValue;
      this.percentageChange = summary.percentageChange;

      const dateIntervalDays = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);

      this.chartData = this.prepareChartData(inHouseData, outsourcedData, dateIntervalDays);
      this.calculateYTicks();
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private prepareChartData(
    inHouseData: MaintenanceInsightsResponse[],
    outsourcedData: MaintenanceInsightsResponse[],
    dateIntervalDays: number
  ): GroupedChartData[] {
    const datasets = [
      { data: inHouseData, label: 'inHouse' },
      { data: outsourcedData, label: 'outsourced' }
    ];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (dateIntervalDays === 0) {
      return TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }

    // For 1 month (up to 31 days): show every day
    if (dateIntervalDays <= 31) {
      return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    // For longer periods: group by weeks
    return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
  }

  private calculateYTicks(): void {
    if (this.chartData.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const allValues = this.chartData.flatMap(d => [d['inHouse'] ?? 0, d['outsourced'] ?? 0]);
    this.yTicks = calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  isUptrendPositive(): boolean {
    return false; // Cost increase is negative
  }

  hasData(): boolean {
    return this.chartData.length > 0 && this.chartData.some(item =>
      Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      })
    );
  }

  close(): void {
    this.activeModal.close();
  }
}